import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { useAuth } from "../../../app/providers/AuthProvider";
import { formatApiError } from "../../../services/api";
import { LanguageSelector } from "../../../components/layout/LanguageSelector";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

function makeSchema(t) {
  return z.object({
    email: z
      .string()
      .min(1, t("auth.email_required"))
      .email(t("auth.email_invalid")),
    password: z.string().min(1, t("auth.password_required")),
  });
}

export default function LoginPage() {
  const { t } = useTranslation();
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(makeSchema(t)),
    defaultValues: { email: "", password: "" },
    mode: "onSubmit",
  });

  if (status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const user = await login(values.email, values.password);
      // login() already applied the server-stored language; translate now.
      toast.success(t("auth.login_success", { name: user.name, lng: user.language }));
      // Bug 2: always land on /dashboard after login, regardless of the
      // URL that triggered the redirect. Removes the "return to intended
      // URL" behaviour.
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let message;
      if (status === 429 && detail?.code === "LOGIN_LOCKED") {
        const minutes = Math.ceil((detail.retry_after || 0) / 60);
        message = t("auth.locked", { minutes });
      } else if (status === 401) {
        message = t("auth.invalid_credentials");
      } else {
        message = formatApiError(err, t("common.error_generic"));
      }
      toast.error(message, { id: "login-error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-stretch bg-offwhite"
      data-testid="login-page"
    >
      {/* Left brand panel — charcoal, no gradients */}
      <aside className="hidden w-2/5 flex-col justify-between bg-charcoal p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-champagne flex items-center justify-center text-charcoal font-semibold">
            MH
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold tracking-wide">
              {t("common.app_name")}
            </div>
            <div className="text-[11px] text-white/50">
              {t("common.app_subtitle")}
            </div>
          </div>
        </div>

        <div className="max-w-md">
          <div className="mb-3 h-px w-12 bg-champagne" />
          <h1 className="text-3xl font-semibold leading-tight tracking-tight">
            {t("auth.brand_headline")}
          </h1>
          <p className="mt-4 text-sm text-white/60">
            {t("auth.brand_subline")}
          </p>
        </div>

        <div className="text-[11px] text-white/40">
          {t("auth.internal_only_note")}
        </div>
      </aside>

      {/* Right form panel */}
      <section className="flex flex-1 flex-col">
        <div className="flex items-center justify-end p-6">
          <LanguageSelector variant="light" />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 pb-16">
          <div className="w-full max-w-sm">
            <div className="mb-8 lg:hidden flex items-center gap-3">
              <div className="h-8 w-8 rounded-sm bg-charcoal text-white flex items-center justify-center text-xs font-semibold">
                MH
              </div>
              <div className="text-sm font-semibold">
                {t("common.app_name")}
              </div>
            </div>

            <h2
              className="text-2xl font-semibold text-text-primary"
              data-testid="login-title"
            >
              {t("auth.login_title")}
            </h2>
            <p className="mt-2 text-sm text-text-secondary">
              {t("auth.login_subtitle")}
            </p>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="mt-8 space-y-5"
              noValidate
              data-testid="login-form"
            >
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-text-primary">
                  {t("auth.email_label")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  data-testid="login-email"
                  placeholder={t("auth.email_placeholder")}
                  className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
                  aria-invalid={!!errors.email}
                  {...register("email")}
                />
                {errors.email && (
                  <p
                    className="text-xs text-status-unavailable"
                    data-testid="login-email-error"
                  >
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-text-primary">
                  {t("auth.password_label")}
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  data-testid="login-password"
                  placeholder={t("auth.password_placeholder")}
                  className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
                  aria-invalid={!!errors.password}
                  {...register("password")}
                />
                {errors.password && (
                  <p
                    className="text-xs text-status-unavailable"
                    data-testid="login-password-error"
                  >
                    {errors.password.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="login-submit"
                className="w-full h-10 bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("auth.submitting")}
                  </>
                ) : (
                  t("auth.submit")
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
